import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { 
  Sparkles, 
  Copy, 
  Check, 
  RefreshCw, 
  ArrowLeft, 
  Sliders, 
  Info,
  Lock,
  Compass,
  Trophy,
  Activity
} from 'lucide-react';
import { ToolPageExtras } from '../seo/ToolPageExtras';
import { AdUnit } from './AdUnit';

const AUDIENCE_MAPPINGS = {
  beginner: 'Beginners',
  expert: 'Professionals',
  general: 'Creators'
};

const POWER_WORDS = [
  'Insane', 'Shocking', 'Unbelievable', 'Secret', 'Guaranteed', 
  'Mind-Blowing', 'Ultimate', 'Instant', 'Easiest', 'Hidden',
  'Foolproof', 'Illegal', 'Life-Changing', 'Rare', 'Magic'
];

const TEMPLATES = {
  curiosity: [
    "The {PowerWord} Truth About {Topic} No One Tells You",
    "I Tried {Topic} for 30 Days... Here's What Happened",
    "The Dark Secret Behind {Topic} (And Why It Matters)",
    "Why 99% of {Audience} Fail at {Topic} (And How to Fix It)",
    "What Happens to Your Brain When You Study {Topic}?",
    "Is {Topic} Actually Worth the Hype in 2026?",
    "The {PowerWord} {Topic} Trick That Feels Illegal to Know"
  ],
  how_to: [
    "How to Master {Topic} in 10 Minutes (Step-by-Step)",
    "{Topic} Explained for {Audience} (No Experience Required!)",
    "The Ultimate {Topic} Guide Every {Audience} Needs to Watch",
    "How to Speed Up {Topic} in 3 Simple Steps",
    "Mastering {Topic}: From Absolute Beginner to Pro",
    "The Easiest Way to Learn {Topic} Fast"
  ],
  listicle: [
    "5 {PowerWord} {Topic} Hacks You Wish You Knew Sooner",
    "Top 10 {Topic} Mistakes {Audience} Always Makes",
    "7 {PowerWord} Tools to Automate {Topic} Today",
    "3 {Topic} Secrets That Will Double Your Results",
    "5 Signs You're Doing {Topic} Completely Wrong"
  ],
  fomo: [
    "Stop Doing This with Your {Topic} Right Now!",
    "If You Aren't Using {Topic} in 2026, Watch This...",
    "The Costly {Topic} Mistake That is Ruining Your Progress",
    "Why {Topic} is Dying (And What's Coming Next)",
    "Don't Start {Topic} Until You Watch This Video!",
    "The {PowerWord} Warning Every {Audience} Needs to Hear"
  ],
  versus: [
    "{Topic} vs {TopicAlt}: Which is Actually Better?",
    "Is {Topic} Dead? (We Compared It to {TopicAlt})",
    "I Tested {Topic} against {TopicAlt} (The Results Were {PowerWord})",
    "{Topic} vs {TopicAlt} – The Ultimate Showdown for {Audience}",
    "Why I Switched From {TopicAlt} to {Topic} (Honest Review)"
  ]
};

export const YoutubeTitleGenerator = () => {
  const [topic, setTopic] = useState('React Hooks');
  const [topicAlt, setTopicAlt] = useState('Redux State');
  const [tone, setTone] = useState('curiosity'); // curiosity, how_to, listicle, fomo, versus
  const [audience, setAudience] = useState('beginner'); // beginner, expert, general
  const [generatedTitles, setGeneratedTitles] = useState([]);
  
  const [copiedIdx, setCopiedIdx] = useState(null);

  // Core title generator algorithm
  const generateTitlesList = () => {
    if (!topic) return;

    const selectedTemplates = TEMPLATES[tone] || TEMPLATES.curiosity;
    const audienceLabel = AUDIENCE_MAPPINGS[audience] || 'Creators';
    
    const list = selectedTemplates.map((tpl) => {
      // Pick a random power word to inject
      const randomPower = POWER_WORDS[Math.floor(Math.random() * POWER_WORDS.length)];
      
      let title = tpl
        .replace(/{Topic}/g, topic)
        .replace(/{TopicAlt}/g, topicAlt || 'Legacy Methods')
        .replace(/{Audience}/g, audienceLabel)
        .replace(/{PowerWord}/g, randomPower);

      // Clean up any double spaces or excess punctuation
      title = title.replace(/\s+/g, ' ').trim();
      
      // Analyze length
      let lengthStatus = 'good'; // good (50-70), short (<50), long (>70)
      if (title.length < 40) {
        lengthStatus = 'short';
      } else if (title.length > 70) {
        lengthStatus = 'long';
      }

      return {
        text: title,
        length: title.length,
        status: lengthStatus
      };
    });

    setGeneratedTitles(list);
  };

  // Generate on mount and whenever parameters adjust
  useEffect(() => {
    generateTitlesList();
  }, [topic, topicAlt, tone, audience]);

  const copyToClipboard = (text, idx) => {
    navigator.clipboard.writeText(text);
    setCopiedIdx(idx);
    setTimeout(() => setCopiedIdx(null), 2000);
  };

  return (
    <div style={{
      maxWidth: '1200px',
      margin: '0 auto',
      width: '100%',
      paddingBottom: '60px'
    }}>
      
      {/* Breadcrumbs Navigation */}
      <div style={{
        display: 'flex',
        alignItems: 'center',
        gap: '8px',
        marginBottom: '24px',
        fontSize: '0.9rem'
      }}>
        <Link to="/" style={{
          color: 'var(--text-secondary)',
          textDecoration: 'none',
          display: 'flex',
          alignItems: 'center',
          gap: '4px',
          transition: 'var(--transition-smooth)'
        }} className="breadcrumb-back">
          <ArrowLeft size={16} /> Dashboard
        </Link>
        <span style={{ color: 'var(--text-muted)' }}>/</span>
        <span style={{ color: 'var(--primary)', fontWeight: 600 }}>YouTube Title Generator</span>
      </div>



      {/* Workspace Grid */}
      <div style={{
        display: 'grid',
        gridTemplateColumns: '1fr',
        gap: '32px',
        alignItems: 'start'
      }} className="titles-workspace-grid">
        
        {/* Left Column: Parameter controls */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: '24px' }}>
          
          <div style={cardStyle}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '8px' }}>
              <Sliders size={18} style={{ color: '#dc2626' }} />
              <h3 style={{ fontSize: '1.15rem', fontWeight: 800, color: 'var(--text-primary)', margin: 0 }}>Parameter Configurations</h3>
            </div>

            <div style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
              
              {/* Topic core input */}
              <div style={inputGroupStyle}>
                <label style={labelStyle}>Core Video Topic / Keyword</label>
                <input 
                  type="text"
                  value={topic}
                  onChange={(e) => setTopic(e.target.value)}
                  placeholder="E.G. Figma UI Design, React Hooks"
                  maxLength={40}
                  style={textInputStyle}
                />
              </div>

              {/* Alternative topic (for comparisons) */}
              {tone === 'versus' && (
                <div style={inputGroupStyle}>
                  <label style={labelStyle}>Alternative Topic (For Comparison)</label>
                  <input 
                    type="text"
                    value={topicAlt}
                    onChange={(e) => setTopicAlt(e.target.value)}
                    placeholder="E.G. Adobe XD, Angular"
                    maxLength={40}
                    style={textInputStyle}
                  />
                </div>
              )}

              {/* Tone angle selectors */}
              <div style={inputGroupStyle}>
                <label style={labelStyle}>Hook / Psychological Angle</label>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(130px, 1fr))', gap: '6px' }}>
                  {[
                    { id: 'curiosity', label: 'Curiosity Gap' },
                    { id: 'how_to', label: 'Step-by-Step' },
                    { id: 'listicle', label: 'List & Hacks' },
                    { id: 'fomo', label: 'Warning / FOMO' },
                    { id: 'versus', label: 'Comparison' }
                  ].map((t) => (
                    <button
                      key={t.id}
                      onClick={() => setTone(t.id)}
                      style={{
                        height: '40px',
                        border: tone === t.id ? '1.5px solid #dc2626' : '1px solid var(--border-color)',
                        borderRadius: '8px',
                        fontSize: '0.78rem',
                        fontWeight: 800,
                        cursor: 'pointer',
                        background: tone === t.id ? 'rgba(220, 38, 38, 0.05)' : '#ffffff',
                        color: tone === t.id ? '#dc2626' : 'var(--text-secondary)',
                        transition: 'var(--transition-smooth)'
                      }}
                    >
                      {t.label}
                    </button>
                  ))}
                </div>
              </div>

              {/* Target Audience selectors */}
              <div style={inputGroupStyle}>
                <label style={labelStyle}>Target Audience Niche</label>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '6px' }}>
                  {[
                    { id: 'beginner', label: 'Beginners' },
                    { id: 'expert', label: 'Pros' },
                    { id: 'general', label: 'General' }
                  ].map((aud) => (
                    <button
                      key={aud.id}
                      onClick={() => setAudience(aud.id)}
                      style={{
                        height: '38px',
                        border: audience === aud.id ? '1.5px solid #dc2626' : '1px solid var(--border-color)',
                        borderRadius: '8px',
                        fontSize: '0.78rem',
                        fontWeight: 800,
                        cursor: 'pointer',
                        background: audience === aud.id ? 'rgba(220, 38, 38, 0.05)' : '#ffffff',
                        color: audience === aud.id ? '#dc2626' : 'var(--text-secondary)',
                        textTransform: 'uppercase',
                        transition: 'var(--transition-smooth)'
                      }}
                    >
                      {aud.label}
                    </button>
                  ))}
                </div>
              </div>

            </div>
          </div>

          {/* Guidelines on optimal CTR titles */}
          <div style={{
            background: '#ffffff',
            border: '1px solid var(--border-color)',
            borderRadius: '20px',
            padding: '24px',
            display: 'flex',
            alignItems: 'flex-start',
            gap: '14px'
          }}>
            <Activity size={20} style={{ color: '#dc2626', flexShrink: 0, marginTop: '2px' }} />
            <div>
              <h4 style={{ margin: '0 0 4px 0', fontSize: '0.95rem', fontWeight: 800, color: 'var(--text-primary)' }}>
                YouTube CTR Optimization Rules
              </h4>
              <p style={{ margin: 0, fontSize: '0.8rem', color: 'var(--text-secondary)', lineHeight: 1.5 }}>
                Aim to keep your titles <strong>under 70 characters</strong>. Titles exceeding this range are frequently truncated in mobile feeds and recommendation scrolls. Injecting <strong>Power Words</strong> triggers immediate emotional interest.
              </p>
            </div>
          </div>

        </div>

        {/* Right Column: Generated lists display */}
        <div style={{
          display: 'flex',
          flexDirection: 'column',
          gap: '24px'
        }} className="titles-display-column">
          
          <div style={{ ...cardStyle, minHeight: '400px' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                <Sparkles size={18} style={{ color: '#dc2626' }} />
                <h3 style={{ fontSize: '1.15rem', fontWeight: 800, color: 'var(--text-primary)', margin: 0 }}>Generated Hooks Suggestions</h3>
              </div>
              <button
                onClick={generateTitlesList}
                style={{
                  border: 'none',
                  background: 'transparent',
                  color: '#dc2626',
                  fontSize: '0.8rem',
                  fontWeight: 700,
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '4px'
                }}
              >
                <RefreshCw size={12} /> Re-shuffle list
              </button>
            </div>

            {/* List of titles */}
            <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
              {generatedTitles.length > 0 ? (
                generatedTitles.map((item, idx) => (
                  <div
                    key={idx}
                    style={{
                      background: 'var(--bg-secondary)',
                      border: '1px solid var(--border-color)',
                      borderRadius: '12px',
                      padding: '16px 20px',
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                      gap: '16px',
                      transition: 'border-color 0.15s'
                    }}
                    className="title-row-card"
                  >
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '8px', flex: 1 }}>
                      <span style={{
                        fontSize: '1rem',
                        fontWeight: 700,
                        color: 'var(--text-primary)',
                        lineHeight: 1.4
                      }}>
                        {item.text}
                      </span>
                      
                      {/* Character length indicator label */}
                      <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                        <span style={{ fontSize: '0.7rem', color: 'var(--text-muted)', fontWeight: 700 }}>
                          Length: <strong>{item.length} chars</strong>
                        </span>
                        
                        <span style={{
                          fontSize: '0.65rem',
                          fontWeight: 800,
                          padding: '2px 8px',
                          borderRadius: '4px',
                          background: item.status === 'good' ? 'rgba(34, 197, 94, 0.08)' : item.status === 'short' ? 'rgba(234, 179, 8, 0.08)' : 'rgba(239, 68, 68, 0.08)',
                          color: item.status === 'good' ? '#22c55e' : item.status === 'short' ? '#eab308' : '#ef4444'
                        }}>
                          {item.status === 'good' ? 'OPTIMAL SIZE' : item.status === 'short' ? 'A BIT SHORT' : 'TRUNCATION RISK'}
                        </span>
                      </div>
                    </div>

                    <button
                      onClick={() => copyToClipboard(item.text, idx)}
                      style={{
                        background: copiedIdx === idx ? '#059669' : 'rgba(15, 23, 42, 0.03)',
                        color: copiedIdx === idx ? '#ffffff' : 'var(--text-primary)',
                        border: 'none',
                        borderRadius: '8px',
                        padding: '10px 16px',
                        fontSize: '0.8rem',
                        fontWeight: 700,
                        cursor: 'pointer',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '6px',
                        transition: 'var(--transition-smooth)'
                      }}
                      className="copy-title-row-btn"
                    >
                      {copiedIdx === idx ? <Check size={14} /> : <Copy size={14} />}
                      {copiedIdx === idx ? 'Copied!' : 'Copy'}
                    </button>
                  </div>
                ))
              ) : (
                <div style={{ color: 'var(--text-muted)', fontSize: '0.85rem', textAlign: 'center', padding: '40px' }}>
                  Please enter a topic keyword to generate title hooks...
                </div>
              )}
            </div>

          </div>



        </div>

      </div>

      {/* Styles details overrides */}
      <style>{`
        @media (min-width: 992px) {
          .titles-workspace-grid {
            grid-template-columns: 380px 1fr !important;
          }
        }

        .title-row-card:hover {
          border-color: rgba(220, 38, 38, 0.15) !important;
        }

        .copy-title-row-btn:hover {
          opacity: 0.9;
        }
      `}</style>

      {/* SEO: FAQ & Related Tools */}
      <AdUnit />
      <ToolPageExtras toolId="youtube-title-generator" />
    </div>
  );
};

// Static definitions
const cardStyle = {
  background: '#ffffff',
  border: '1px solid var(--border-color)',
  borderRadius: '20px',
  padding: '24px',
  display: 'flex',
  flexDirection: 'column',
  gap: '18px',
  boxShadow: 'var(--shadow-sm)'
};

const inputGroupStyle = {
  display: 'flex',
  flexDirection: 'column',
  gap: '8px'
};

const labelStyle = {
  fontSize: '0.75rem',
  fontWeight: 800,
  color: 'var(--text-secondary)',
  textTransform: 'uppercase',
  letterSpacing: '0.04em'
};

const textInputStyle = {
  width: '100%',
  padding: '12px 16px',
  borderRadius: '10px',
  border: '1px solid var(--border-color)',
  fontSize: '0.95rem',
  fontWeight: 600,
  outline: 'none',
  background: 'var(--bg-secondary)',
  color: 'var(--text-primary)',
  boxSizing: 'border-box'
};

export default YoutubeTitleGenerator;
